import React, { useState, useMemo } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input } from 'reactstrap';
import { toast } from 'react-toastify';
import fileDownload from 'js-file-download';
import { TextEncoder } from 'text-encoding';
import { parse as parseCsv, unparse as unparseCsv } from 'papaparse';
import { chunk, uniq, keyBy } from 'lodash';
import { useToggle } from 'react-use';

import firebase from '../../firebase';
import { batch } from '../../shared/firebase';
import { readCsvFile } from '../../util';

const db = firebase.firestore();
const locationsRef = db.collection('locations');
const inventoriesRef = db.collection('inventories');

const sampleRows = [
  { oldSortKey: 'A-01', newSortKey: 'A-99', name: '' },        // キーのみ変更
  { oldSortKey: 'A-02', newSortKey: 'A-03', name: '' },        // A-02 と A-03 の入れ替え（その1）
  { oldSortKey: 'A-03', newSortKey: 'A-02', name: '' },        // A-02 と A-03 の入れ替え（その2）
  { oldSortKey: 'B-01', newSortKey: '', name: '入口前ラック' }, // 名称のみ変更
];

const onClickDownloadSample = () => {
  const encoder = new TextEncoder('Shift_JIS', { NONSTANDARD_allowLegacyEncoding: true });
  const fields = ['oldSortKey', 'newSortKey', 'name'];
  const content = encoder.encode(unparseCsv({ fields, data: sampleRows.map(_ => fields.map(f => _[f])) }));
  fileDownload(content, 'ロケーション一括編集_サンプル.csv');
};

export default function LocationsBatchEditModal({ isOpen, onClickClose, onFinish }) {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [validation, setValidation] = useState(null); // { items: [...], errors: [...] }
  const [isValidating, toggleValidating] = useToggle(false);
  const [isSubmitting, toggleSubmitting] = useToggle(false);

  const reset = () => {
    setRows([]);
    setFileName('');
    setValidation(null);
  };

  const onSelectFile = async ({ target, target: { files: [file] } }) => {
    if (!file) return;
    reset();
    setFileName(file.name);
    try {
      const content = await readCsvFile(file);
      const { data } = parseCsv(content, { header: true });
      const parsed = data
        .map(_ => ({
          oldSortKey: (_.oldSortKey ?? '').trim(),
          newSortKey: (_.newSortKey ?? '').trim(),
          name: (_.name ?? '').trim(),
        }))
        .filter(_ => _.oldSortKey || _.newSortKey || _.name);
      setRows(parsed);
      await validate(parsed);
    } catch (e) {
      console.error(e);
      toast.error('CSVの読み込みに失敗しました');
    }
    target.value = '';
  };

  const validate = async (parsed) => {
    toggleValidating(true);
    try {
      // 対象となる「変更前キー」をすべて取得
      const oldKeys = uniq(parsed.map(_ => _.oldSortKey).filter(Boolean));
      const chunks = chunk(oldKeys, 10);
      const found = (await Promise.all(
        chunks.map(_ => locationsRef.where('sortKey', 'in', _).get())
      )).flatMap(_ => _.docs.map(d => ({ id: d.id, ...d.data() })));
      const locationByOldKey = keyBy(found, 'sortKey');

      // 変更後の最終的なキー（指定がなければ既存のまま）
      const items = parsed.map((row, i) => {
        const existing = locationByOldKey[row.oldSortKey];
        const finalSortKey = row.newSortKey || row.oldSortKey;
        const finalName = row.name || existing?.name || finalSortKey;
        return { ...row, i, existing, finalSortKey, finalName };
      });

      // 変更後キーの重複チェック（入力内）
      const finalKeyCounts = items.reduce((x, _) => ({ ...x, [_.finalSortKey]: (x[_.finalSortKey] || 0) + 1 }), {});

      // 変更後キーが、今回の編集対象でない既存ロケと衝突しないかチェック
      const finalKeys = uniq(items.map(_ => _.finalSortKey).filter(Boolean));
      const editedOldKeys = new Set(items.map(_ => _.oldSortKey));
      const finalKeyChunks = chunk(finalKeys, 10);
      const existingByFinalKey = keyBy(
        (await Promise.all(
          finalKeyChunks.map(_ => locationsRef.where('sortKey', 'in', _).get())
        )).flatMap(_ => _.docs.map(d => ({ id: d.id, ...d.data() }))),
        'sortKey'
      );

      const errors = items.flatMap((item) => {
        const rowErrors = [];
        if (!item.oldSortKey) rowErrors.push('変更前キーが空です');
        else if (!item.existing) rowErrors.push(`変更前キー「${item.oldSortKey}」のロケが存在しません`);
        if (!item.finalSortKey) rowErrors.push('変更後キーが空です');
        if (finalKeyCounts[item.finalSortKey] > 1) rowErrors.push(`変更後キー「${item.finalSortKey}」が入力内で重複しています`);
        // 編集対象外の既存ロケと衝突
        const collided = existingByFinalKey[item.finalSortKey];
        if (collided && !editedOldKeys.has(collided.sortKey)) {
          rowErrors.push(`変更後キー「${item.finalSortKey}」は既存の別ロケと重複します`);
        }
        return rowErrors.map(message => ({ i: item.i, message }));
      });

      setValidation({ items, errors });
    } catch (e) {
      console.error(e);
      toast.error('検証に失敗しました');
      setValidation(null);
    }
    toggleValidating(false);
  };

  const changedItems = useMemo(() => {
    if (!validation) return [];
    return validation.items.filter(_ => _.existing && (_.finalSortKey !== _.existing.sortKey || _.finalName !== _.existing.name));
  }, [validation]);

  const hasErrors = (validation?.errors.length ?? 0) > 0;
  const isSubmittable = validation != null && !hasErrors && !isValidating && !isSubmitting && changedItems.length > 0;

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!isSubmittable) return;
    toggleSubmitting(true);
    try {
      // 1. ロケーション本体を更新
      await batch(db, changedItems, (b, _) => b.update(locationsRef.doc(_.existing.id), { sortKey: _.finalSortKey, name: _.finalName }));

      // 2. キーが変わったロケに紐づく在庫の locationKey を更新
      const keyChanged = changedItems.filter(_ => _.finalSortKey !== _.existing.sortKey);
      const inventoryUpdates = await keyChanged.reduce(async (x, item) => {
        const prevs = await x;
        const { docs } = await inventoriesRef.where('locationId', '==', item.existing.id).get();
        return [...prevs, ...docs.map(d => ({ ref: d.ref, locationKey: item.finalSortKey }))];
      }, Promise.resolve([]));
      if (inventoryUpdates.length > 0) {
        await batch(db, inventoryUpdates, (b, _) => b.update(_.ref, { locationKey: _.locationKey }));
      }

      toast.success(`${changedItems.length}件のロケーションを更新しました（在庫 ${inventoryUpdates.length} 件のロケキーも更新）`);
      reset();
      onFinish && onFinish();
      onClickClose();
    } catch (e) {
      console.error(e);
      toast.error(e.message || '失敗しました');
    }
    toggleSubmitting(false);
  };

  const errorsByRow = useMemo(() => {
    if (!validation) return {};
    return validation.errors.reduce((x, _) => ({ ...x, [_.i]: [...(x[_.i] || []), _.message] }), {});
  }, [validation]);

  return (
    <Modal isOpen={isOpen} size="xl">
      <ModalHeader>ロケーションCSV一括編集</ModalHeader>
      <Form onSubmit={onSubmit}>
        <ModalBody>
          <FormGroup>
            <div className="d-flex justify-content-between align-items-end mb-1">
              <Label className="m-0">CSVファイル</Label>
              <Button color="link" size="sm" className="p-0" onClick={onClickDownloadSample} type="button">
                <span className="fas fa-download mr-1" />サンプルCSVをダウンロード
              </Button>
            </div>
            <Input type="file" accept=".csv" onChange={onSelectFile} disabled={isSubmitting} />
            <div className="text-muted small mt-1">
              ヘッダー行：<code>oldSortKey,newSortKey,name</code><br />
              ・<code>oldSortKey</code>（変更前キー）必須。既存ロケの特定に使用。<br />
              ・<code>newSortKey</code>（変更後キー）省略時はキーを変更しない。<br />
              ・<code>name</code>（変更後名称）省略時は名称を変更しない。<br />
              ・A→B、B→A のような入れ替えにも対応。<br />
              ・変更後キーは在庫データのロケキーにも反映。
            </div>
          </FormGroup>
          {isValidating && <div className="text-muted"><span className="fas fa-spin fa-spinner mr-1" />検証中…</div>}
          {
            validation && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <strong>{fileName}（{rows.length}行）</strong>
                  <span className={hasErrors ? 'text-danger' : 'text-success'}>
                    {hasErrors ? `${validation.errors.length}件のエラー` : `更新対象 ${changedItems.length}件`}
                  </span>
                </div>
                <div style={{ maxHeight: 360, overflowY: 'auto' }} className="border rounded p-2">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: 50 }}>#</th>
                        <th>変更前キー</th>
                        <th>変更後キー</th>
                        <th>変更後名称</th>
                        <th>状態</th>
                      </tr>
                    </thead>
                    <tbody>
                      {
                        validation.items.map((item) => {
                          const rowErrors = errorsByRow[item.i] || [];
                          const hasRowError = rowErrors.length > 0;
                          const isChanged = item.existing && (item.finalSortKey !== item.existing.sortKey || item.finalName !== item.existing.name);
                          return (
                            <tr key={item.i} className={hasRowError ? 'table-danger' : ''}>
                              <td>{item.i + 1}</td>
                              <td>{item.oldSortKey}</td>
                              <td>{item.finalSortKey}{item.existing && item.finalSortKey !== item.existing.sortKey && <span className="text-primary small ml-1">（変更）</span>}</td>
                              <td>{item.finalName}{item.existing && item.finalName !== item.existing.name && <span className="text-primary small ml-1">（変更）</span>}</td>
                              <td>
                                {hasRowError
                                  ? <span className="text-danger small">{rowErrors.join(' / ')}</span>
                                  : isChanged
                                    ? <span className="text-success small">更新</span>
                                    : <span className="text-muted small">変更なし</span>}
                              </td>
                            </tr>
                          );
                        })
                      }
                    </tbody>
                  </table>
                </div>
                {hasErrors && <div className="text-danger small mt-2">エラーがあるため保存できません。</div>}
              </div>
            )
          }
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={onClickClose} disabled={isSubmitting}>閉じる</Button>
          <Button type="submit" color="primary" onClick={onSubmit} disabled={!isSubmittable}>
            {isSubmitting && <span className="fas fa-spin fa-spinner mr-1" />}
            {changedItems.length > 0 ? `${changedItems.length}件を更新` : '更新'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};
